import {
  ButtonCore,
  ButtonCoreBaseProps,
  ButtonCoreFullWidthProps,
  ButtonCoreWithoutSelectedProps,
} from './ButtonCore';
import { ButtonIconProps } from 'omakase/Button/buttonIcons';
import { IconArrowDownSmall } from 'ui/icons';
import { IconChevronSmallDown } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
} from '../utils/OmakasePolymorphicComponent';
import Anchor from 'components/Anchor';
import React from 'react';

interface ButtonAdditionalProps {
  /**
   * An optional prop that indicates whether or not to override the iconEnd with a small down facing arrow
   */
  disclosure?: boolean;
}

export type ButtonProps = ButtonCoreBaseProps &
  ButtonCoreWithoutSelectedProps &
  ButtonCoreFullWidthProps &
  ButtonAdditionalProps &
  ButtonIconProps;

const ButtonDefaultElement = 'button';
type ButtonTypeGuard = 'button' | typeof Anchor;

const Button: OmakasePolymorphicComponent<
  ButtonProps,
  typeof ButtonDefaultElement,
  ButtonTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof ButtonDefaultElement>(
    {
      children,
      component,
      disclosure,
      iconEnd,
      ...rest
    }: OmakasePolymorphicComponentProps<ButtonProps, E, ButtonTypeGuard>,
    ref: React.Ref<HTMLButtonElement> | undefined
  ) => {
    const Component: React.ElementType = component || ButtonDefaultElement;

    const iconEndElement = disclosure
      ? {
          icon: (
            <IconMigrationUtil
              legacyIcon={<IconArrowDownSmall />}
              newIcon={<IconChevronSmallDown />}
            />
          ),
          slimPadding: true,
        }
      : iconEnd
      ? { icon: iconEnd }
      : undefined;

    return (
      <ButtonCore component={Component} iconEnd={iconEndElement} ref={ref} {...rest}>
        {children}
      </ButtonCore>
    );
  }
);

export default Button;
